import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { intakeText } from "@/i18n/propertyIntakeTranslations";
import { intakePublicationText } from "@/i18n/intakePublicationTranslations";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const blank = {
  owner_name: "", owner_phone: "", owner_email: "", owner_contact_id: "",
  property_type: "", address: "", city: "", postal_code: "",
  area_m2: "", asking_price: "", occupancy: "", documentation: "",
  notes: "", next_step: "", status: "novo",
};
const inputClass = "w-full border border-slate-300 rounded-sm px-3 py-2 bg-white";

export default function PropertyIntakes() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const tr = intakeText(i18n.language);
  const pub = intakePublicationText(i18n.language);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [reviewFlags, setReviewFlags] = useState({ owner_authorization_confirmed: false, documents_reviewed: false });
  const publication = useQuery({
    queryKey: ["property-intake-publication", reviewing],
    queryFn: async () => (await api.get(`/property-intakes/${reviewing}/publication`)).data,
    enabled: !!reviewing,
  });
  useEffect(() => {
    if (publication.data) setReviewFlags({
      owner_authorization_confirmed: !!publication.data.checks.owner_authorization,
      documents_reviewed: !!publication.data.checks.documents_reviewed,
    });
  }, [publication.data]);
  const convert = useMutation({
    mutationFn: async (id) => (await api.post(`/property-intakes/${id}/property`)).data,
    onSuccess: async (data, id) => {
      await client.invalidateQueries({ queryKey: ["property-intakes"] });
      await client.invalidateQueries({ queryKey: ["properties"] });
      setReviewing(id);
      toast.success(data.created ? pub.created : pub.open);
    },
    onError: (error) => toast.error(typeof error.response?.data?.detail === "string" ? error.response.data.detail : pub.createError),
  });
  const saveReview = useMutation({
    mutationFn: async () => (await api.put(`/property-intakes/${reviewing}/publication/review`, reviewFlags)).data,
    onSuccess: () => { publication.refetch(); toast.success(pub.saved); },
    onError: () => toast.error(pub.reviewError),
  });
  const publish = useMutation({
    mutationFn: async () => (await api.post(`/property-intakes/${reviewing}/publication/publish`)).data,
    onSuccess: async () => {
      await publication.refetch();
      await client.invalidateQueries({ queryKey: ["properties"] });
      toast.success(pub.publishedMessage);
    },
    onError: () => { publication.refetch(); toast.error(pub.publishError); },
  });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts", {}], queryFn: async () => (await api.get("/contacts")).data });
  const { data: rows = [], isLoading, isError } = useQuery({
    queryKey: ["property-intakes"],
    queryFn: async () => (await api.get("/property-intakes")).data,
  });
  const save = useMutation({
    mutationFn: async (data) => {
      const payload = { ...data, area_m2: data.area_m2 === "" ? null : Number(data.area_m2), asking_price: data.asking_price === "" ? null : Number(data.asking_price) };
      return editing ? api.put(`/property-intakes/${editing}`, payload) : api.post("/property-intakes", payload);
    },
    onSuccess: () => { client.invalidateQueries({ queryKey: ["property-intakes"] }); setForm(null); setEditing(null); toast.success(tr.saved); },
    onError: (error) => toast.error(error.response?.data?.detail || tr.saveError),
  });
  const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const field = (key, label, type = "text", required = false) => (
    <label className="block text-sm text-slate-700" key={key}>{label}
      <input className={`${inputClass} mt-1`} type={type} required={required} value={form[key] ?? ""} onChange={change(key)} />
    </label>
  );
  const download = async (row) => {
    setDownloading(row.id);
    try {
      const response = await api.get(`/property-intakes/${row.id}/pdf`, { responseType: "blob", params: { lang: i18n.language } });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `angariacao-${row.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) { toast.error(tr.pdfError); }
    finally { setDownloading(null); }
  };

  return <div className="space-y-6">
    <div className="flex flex-wrap justify-between items-center gap-3">
      <div><h1 className="font-serif text-3xl text-[#0B2545]">{tr.title}</h1>
        <p className="text-slate-600 mt-1">{tr.subtitle}</p></div>
      <Button onClick={() => { setEditing(null); setForm({ ...blank }); }} className="bg-[#0B2545] text-white">{tr.newBrief}</Button>
    </div>
    {form && <form onSubmit={(event) => { event.preventDefault(); save.mutate(form); }} className="bg-white border border-slate-200 p-6 space-y-5">
      <h2 className="font-serif text-xl">{editing ? tr.editBrief : tr.newAcquisition}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="block text-sm text-slate-700">{tr.existingContact}
          <select className={`${inputClass} mt-1`} value={form.owner_contact_id} onChange={(event) => {
            const contact = contacts.find((item) => item.id === event.target.value);
            setForm((current) => ({ ...current, owner_contact_id: event.target.value,
              ...(contact ? { owner_name: contact.name || "", owner_phone: contact.mobile || contact.phone || "", owner_email: contact.email || "" } : {}) }));
          }}>
            <option value="">{tr.manualOwner}</option>
            {contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
          </select>
        </label>
        {field("owner_name", tr.ownerName, "text", true)}
        {field("owner_phone", tr.phone)}
        {field("owner_email", tr.email, "email")}
        {field("property_type", tr.type)}
        {field("address", tr.address, "text", true)}
        {field("city", tr.city)}
        {field("postal_code", tr.postal)}
        {field("area_m2", tr.area, "number")}
        {field("asking_price", tr.price, "number")}
        {field("occupancy", tr.occupancy)}
        <label className="block text-sm text-slate-700">{tr.status}
          <select className={`${inputClass} mt-1`} value={form.status} onChange={change("status")}>
            {Object.entries(tr.states).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      {[ ["documentation", tr.docs], ["notes", tr.notes], ["next_step", tr.nextStep] ].map(([key, label]) =>
        <label key={key} className="block text-sm text-slate-700">{label}
          <textarea className={`${inputClass} mt-1`} rows={key === "notes" ? 4 : 2} value={form[key]} onChange={change(key)} />
        </label>)}
      <div className="flex gap-2"><Button type="submit" disabled={save.isPending} className="bg-[#0B2545] text-white">{save.isPending ? tr.saving : tr.save}</Button>
        <Button type="button" variant="outline" onClick={() => { setForm(null); setEditing(null); }}>{tr.cancel}</Button></div>
    </form>}
    {isLoading && <p>{tr.loading}</p>}
    {isError && <p className="text-red-700">{tr.loadError}</p>}
    {!isLoading && !isError && rows.length === 0 && <p className="bg-white border p-6">{tr.empty}</p>}
    {reviewing && <section className="bg-white border border-slate-200 p-5 space-y-4" aria-label={pub.review}>
      <div className="flex justify-between items-start gap-3">
        <div><h2 className="font-serif text-xl text-[#0B2545]">{pub.review}</h2><p className="text-sm text-slate-600">{pub.notice}</p></div>
        <Button type="button" variant="outline" onClick={() => setReviewing(null)}>{pub.hide}</Button>
      </div>
      {publication.isLoading && <p>{pub.loading}</p>}
      {publication.isError && <p className="text-red-700">{pub.error}</p>}
      {publication.data && <>
        <p className="font-medium">{publication.data.publication_status === "published" ? pub.published : pub.draft}</p>
        <div className="grid gap-2 sm:grid-cols-2 text-sm">
          {Object.entries(publication.data.checks).map(([key, done]) =>
            <div key={key} className={`border p-2 ${done ? "border-emerald-200 text-emerald-800" : "border-amber-200 text-amber-900"}`}>
              <span aria-hidden="true">{done ? "✓" : "○"}</span> {pub[key]}
            </div>)}
        </div>
        <p className="text-sm text-slate-600">{pub.editProperty}</p>
        {publication.data.publication_status !== "published" && (user?.role === "admin" ? <>
          <label className="flex gap-2 items-start text-sm"><input type="checkbox" checked={reviewFlags.owner_authorization_confirmed} onChange={(event) => setReviewFlags((v) => ({ ...v, owner_authorization_confirmed: event.target.checked }))} />{pub.confirmOwner}</label>
          <label className="flex gap-2 items-start text-sm"><input type="checkbox" checked={reviewFlags.documents_reviewed} onChange={(event) => setReviewFlags((v) => ({ ...v, documents_reviewed: event.target.checked }))} />{pub.confirmDocs}</label>
          <Button type="button" variant="outline" disabled={saveReview.isPending} onClick={() => saveReview.mutate()}>{saveReview.isPending ? pub.savingReview : pub.saveReview}</Button>
          <Button type="button" className="ml-2 bg-[#0B2545]" disabled={!publication.data.ready || saveReview.isPending || publish.isPending} onClick={() => {
            if (window.confirm(pub.confirmPublish)) publish.mutate();
          }}>{publish.isPending ? pub.publishing : pub.publish}</Button>
        </> : <p className="text-sm text-amber-800">{pub.adminOnly}</p>)}
        <Button type="button" variant="outline" className="ml-2" onClick={() => navigate(`/properties/${publication.data.property_id}`)}>{pub.open}</Button>
      </>}
    </section>}
    <div className="grid gap-3">{rows.map((row) => <article key={row.id} className="bg-white border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-3">
      <div><h2 className="font-serif text-xl text-[#0B2545]">{row.address}</h2>
        <p className="text-sm text-slate-600">{row.owner_name} · {row.city || tr.noCity} · {tr.states[row.status] || row.status}</p></div>
      <div className="flex gap-2"><Button variant="outline" onClick={() => { setEditing(row.id); setForm({ ...blank, ...row }); }}>{tr.edit}</Button>
        <Button variant="outline" disabled={downloading === row.id} onClick={() => download(row)}>{downloading === row.id ? tr.downloading : tr.download}</Button>
        {row.property_id ? <><Button variant="outline" onClick={() => navigate(`/properties/${row.property_id}`)}>{pub.open}</Button>
          <Button variant="outline" onClick={() => setReviewing(row.id)}>{pub.review}</Button></> :
          <Button type="button" disabled={convert.isPending} onClick={() => convert.mutate(row.id)}>{convert.isPending && convert.variables === row.id ? pub.creating : pub.create}</Button>}
      </div>
    </article>)}</div>
  </div>;
}
